import { ReactNode, useContext } from 'react';

import { cn } from 'utils/cn';
import { GLYPH_OWN_SIZE } from 'components/buttons/buttonStyle';
import { CloseOutlined } from 'components/icons';
import { Button } from 'components/ui/button';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { DialogDescription, DialogTitle } from 'components/ui/dialog';
import { InStackedModalContext } from './ModalProvider';

interface Props {
    title: string;
    onCancel: () => void;
    subtitle?: string;
    /** In the new design, the kit dialog's header: no rule under it and the larger title. Classic is unchanged. */
    kit?: boolean;
    /** A line under the title, which the new design draws; classic shows none. */
    description?: ReactNode;
}

/** The kit dialog's header, in the new design only, for a modal that opts in with `kit`. */
const KIT_HEADER = 'new:items-start new:border-b-0 new:pt-5 new:pr-4 new:pb-0 new:pl-6';
const KIT_TITLE = 'new:text-lg new:leading-7';

const TITLE_CLASS = 'text-h5 m-0';

/**
 * A modal's title row. In a modal of the stack the title is the kit dialog's own, which gives the dialog its name;
 * that part throws outside a dialog, so anywhere else the title is the same heading on its own.
 */
export const ModalHeader = ({ title, onCancel, subtitle, kit, description }: Props): JSX.Element => {
    const inStackedModal = useContext(InStackedModalContext);
    const titleRow = (
        <div className={cn('flex items-baseline gap-2', kit && 'new:pt-1')}>
            {inStackedModal ? (
                // `font-semibold` is the title's own weight, said again over the kit title's medium.
                <DialogTitle className={cn(TITLE_CLASS, 'font-semibold', kit && KIT_TITLE)}>{title}</DialogTitle>
            ) : (
                <h2 className={cn(TITLE_CLASS, kit && KIT_TITLE)}>{title}</h2>
            )}
            {subtitle && <span className="text-body2 text-fg-secondary">{subtitle}</span>}
        </div>
    );
    return (
        <div
            className={cn(
                'border-divider flex items-center justify-between border-b py-2.5 pr-[15px] pl-[23px]',
                kit && KIT_HEADER
            )}
        >
            {description ? (
                <div className="flex min-w-0 flex-col gap-1">
                    {titleRow}
                    <NewDesignDescription inStackedModal={inStackedModal}>{description}</NewDesignDescription>
                </div>
            ) : (
                titleRow
            )}
            <Button
                variant="ghost"
                size="icon-lg"
                aria-label="close"
                className={cn(GLYPH_OWN_SIZE, 'rounded-full')}
                onClick={onCancel}
            >
                <CloseOutlined />
            </Button>
        </div>
    );
};

/** The header's description in the new design: the dialog's own where it has one, which describes the dialog. */
function NewDesignDescription({ inStackedModal, children }: { inStackedModal: boolean; children: ReactNode }) {
    const isNew = useIsNewDesign();
    if (!isNew) {
        return null;
    }
    return inStackedModal ? (
        <DialogDescription render={<div />}>{children}</DialogDescription>
    ) : (
        <div className="text-muted-foreground text-sm">{children}</div>
    );
}
